'use client';
import {
  Button,
  Input,
  MinthrSelect,
  Sidebar,
  TextArea,
} from 'components/common';
import { useSidebarManager } from 'contexts/SidebarManagerContext';
import {
  createIntervention,
  getInterventionUsers,
} from 'features/material/services/interventionServices';
import { InterventionCreate } from 'features/material/types/interventionTypes';
import { Form, Formik } from 'formik';
import { useParams } from 'next/navigation';
import React, { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import InfoIntervention from './InfoIntervention';
import FileUpload from 'components/common/FileUpload';
import { appendToFormData } from 'utils/globalMethodes';
import { getMaterials } from 'features/material/services/hardwareServices';
import * as Yup from 'yup';
import { getSoftwares } from 'features/material/services/softwareServices';
import MintErrorMessage from 'components/common/MintErrorMessage';

interface CreateInterventionProps {
  id?: number;
  material?: '1' | '0' | '2';
  refetch?: () => void;
  Fk_DemadeBy?: string;
  toggleRefreshDT?: () => void;
}
const CreateIntervention: FC<SideBarProps<CreateInterventionProps>> = ({
  sidebar,
  setSidebarWidth,
  sidebarWidth,
  params,
}) => {
  const { t } = useTranslation();
  const { id } = useParams();
  const { openSidebar, closeSidebar } = useSidebarManager();
  const [users, setUsers] = useState<{ id: string; text: string }[]>([]);
  const [error, setError] = useState<string>('');
  const [materials, setMaterials] = useState<{ id: string; text: string }[]>(
    [],
  );
  const [softwares, setSoftwares] = useState<{ id: string; text: string }[]>(
    [],
  );

  const getUsers = async () => {
    const res = await getInterventionUsers({
      searchValue: '',
      lenght: 10,
      currentList: 0,
      id: 0,
      roleName: '',
      userid: '',
      roleId: '',
      ids: [],
      assigend: true,
      selectAll: true,
      isGroup: true,
      isApprovalEvpLevel: true,
    });
    if (res.success) {
      setUsers(res.data);
    } else {
      toast.error(res.message);
    }
  };

  const fetchMaterials = async () => {
    const res = await getMaterials();
    if (res.success) {
      setMaterials(res.data);
    }
  };

  const fetchSoftwares = async () => {
    const res = await getSoftwares();
    if (res.success) {
      setSoftwares(res.data);
    }
  };

  const initialValues: InterventionCreate = {
    id: 0,
    fk_DemadeBy: params?.Fk_DemadeBy ?? id?.toString() ?? '',
    hardware: params?.material === '1' ? 1 : params?.material === '0' ? 0 : 2,
    fk_Material: 0,
    title: '',
    material_Title: '',
    priority: '',
    type: '',
    typeIntervention: '',
    nature: '',
    description: '',
    step: '',
    nbrActions: 0,
    history_description: '',
    statut: '',
    isProfile: false,
    fullModal: true,
    generale: true,
    profileId: '',
    demadendById: '',
    files: [],
    filesNames: [],
  };

  const validationSchema = Yup.object().shape({
    fk_DemadeBy: Yup.string().required(t('RequiredAttribute')),
    type: Yup.string().required(t('RequiredAttribute')),
    title: Yup.string().trim().required(t('RequiredAttribute')),
    description: Yup.string().trim().required(t('RequiredAttribute')),
    fk_Material: Yup.number().when('hardware', {
      is: (val: number) => val === 0 || val === 1,
      then: (schema) =>
        schema
          .required(t('RequiredAttribute'))
          .min(1, t('RequiredAttribute'))
          .test('not-zero', t('RequiredAttribute'), (value) => value !== 0),
      otherwise: (schema) => schema.notRequired(),
    }),
  });

  const handleSubmit = async (values: InterventionCreate) => {
    if (params?.Fk_DemadeBy) {
      values.fk_DemadeBy = params?.Fk_DemadeBy;
    }
    const formData = appendToFormData(values);
    const res = await createIntervention(formData);
    if (res.success) {
      toast.success(t('Default.succesMessage'));
      closeSidebar(CreateIntervention);
      params?.refetch?.();
      params?.toggleRefreshDT?.();
      openSidebar(InfoIntervention, true, {
        id: res.value as number,
        refetch: params?.refetch ?? (() => {}),
        toggleRefreshDT: params?.toggleRefreshDT,
      });
    } else {
      setError(res.message);
    }
  };

  useEffect(() => {
    getUsers();
    fetchMaterials();
    fetchSoftwares();
  }, []);

  return (
    <Sidebar
      size="m"
      type="primary"
      sidebar={sidebar}
      setSidebarWidth={setSidebarWidth}
      sidebarWidth={sidebarWidth}
    >
      <div className="minthr-flex minthr-flex-items-center minthr-pb-2 minthr-mb-4 minthr-border-b minthr-border-b-3">
        <div className="minthr-icon-bg minthr-icon-bg-l minthr-icon-bg-secondary minthr-icon-circle minthr-mr-3">
          <img className="minthr-icon-m" src="/icons/puzzle.svg" alt="" />
        </div>
        <h3>{t('intervention.Create.Title')}</h3>

        <button
          className="minthr-sidebar-close"
          type="button"
          onClick={() => {
            closeSidebar(CreateIntervention);
          }}
        >
          <img src="/icons/close.svg" alt="" />
        </button>
      </div>
      <Formik
        initialValues={initialValues}
        enableReinitialize={true}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ values, setFieldValue, isSubmitting }) => (
          <Form encType="multipart/form-data" className="w-100">
            <div className="minthr-pt-2 minthr-flex minthr-flex-column">
              {error && <MintErrorMessage message={error} />}
              {id || params?.Fk_DemadeBy ? (
                <input name="fk_DemadeBy" value={id} type="hidden" />
              ) : (
                <div className="row g-0 minthr-mb-5">
                  <div className="col-sm-12">
                    <MinthrSelect
                      options={users.map((user) => ({
                        value: user.id,
                        label: user.text,
                      }))}
                      label={t('Conge.Create.f.users')}
                      name="fk_DemadeBy"
                    />
                  </div>
                </div>
              )}

              <div className="row g-0 minthr-mb-5">
                <div className="col-sm-12">
                  <MinthrSelect
                    options={[
                      { value: '0', label: t('Software.Type.incident') },
                      { value: '1', label: t('Software.Type.Request') },
                      { value: '2', label: t('Software.Type.Technical') },
                    ]}
                    label={t('Intervention.filter.TypeIntervention')}
                    name="type"
                  />
                </div>
              </div>

              <div className="row g-0 minthr-mb-5">
                <div className="col-sm-12">
                  <MinthrSelect
                    options={[
                      { value: 2, label: t('Material.Type.General') },
                      { value: 0, label: t('Material.Type.Software') },
                      { value: 1, label: t('Material.Type.Material') },
                    ]}
                    label={t('Itervention.Create.f.Type')}
                    name="hardware"
                    value={params?.material}
                    onchange={(e: { value: number; label: string }) => {
                      setFieldValue('hardware', e.value);
                    }}
                  />
                </div>
              </div>
              {((params?.material === '1' && values.hardware === 1) ||
                values.hardware === 1) && (
                <div className="row g-0 minthr-mb-5 material-div">
                  <div className="col-sm-12 ">
                    <MinthrSelect
                      options={materials.map(
                        (material: { id: string; text: string }) => ({
                          value: material.id,
                          label: material.text,
                        }),
                      )}
                      label={t('Itervention.Fk_Type')}
                      name="fk_Material"
                    />
                  </div>
                </div>
              )}
              {((params?.material === '0' && values.hardware === 0) ||
                values.hardware === 0) && (
                <div className="row g-0 minthr-mb-5 material-div">
                  <div className="col-sm-12 ">
                    <MinthrSelect
                      options={softwares.map(
                        (software: { id: string; text: string }) => ({
                          value: software.id,
                          label: software.text,
                        }),
                      )}
                      label={t('Itervention.Fk_Type')}
                      name="fk_Material"
                    />
                  </div>
                </div>
              )}
              <div className="row g-0 minthr-mb-5">
                <div className="col-sm-12">
                  <Input
                    label={t('Itervention.Create.f.Title')}
                    name="title"
                    type="text"
                  />
                </div>
              </div>
              <div className="row g-0 minthr-mb-5">
                <div className="col-sm-12 ">
                  <TextArea
                    label={t('F.Description')}
                    name="description"
                    rows={4}
                  />
                </div>
              </div>
              <div className="row g-0 minthr-mb-5">
                <div className="col-sm-12">
                  <div id="fileUploadContainer"></div>

                  <FileUpload
                    name="files"
                    allowedFileTypes={[
                      'jpg',
                      'jpeg',
                      'png',
                      'gif',
                      'pdf',
                      'doc',
                      'docx',
                      'xls',
                      'xlsx',
                    ]}
                    initialFiles={values.files}
                    labelClass=""
                    labelText={t('Uplaod.file')}
                    containerClass="minthr-files minthr-border-rounded minthr-bg-primary-opacity minthr-p-6"
                    imageClass="minthr-icon-xl minthr-mb-3"
                    filesContainerClassName=""
                    targetContainerId="fileUploadContainer"
                  />
                </div>
              </div>
            </div>

            <div className="minthr-sidebar-footer minthr-box-shadow minthr-flex minthr-flex-items-center minthr-flex-space-between minthr-py-4 minthr-mx-n5 minthr-px-4 minthr-mt-auto">
              <Button
                type="submit"
                loading={isSubmitting}
                className="minthr-btn minthr-btn-primary minthr-ml-auto"
              >
                {t('submit')}
              </Button>
            </div>
          </Form>
        )}
      </Formik>
    </Sidebar>
  );
};

export default CreateIntervention;
